// src/components/RequestDetails.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { ref, getDownloadURL, uploadBytesResumable } from "firebase/storage";
import { db, storage, auth } from "../firebase";
import {
  Alert,
  Button,
  Card,
  Container,
  Form,
  Spinner,
  Badge,
  ListGroup,
  Row,
  Col,
} from "react-bootstrap";
import { useAuthState } from "react-firebase-hooks/auth";
import { sendRequestStatusEmails } from "../services/emailNotifications";
import "./RequestDetails.css";

/* ====== Estados canónicos en minúsculas + helpers ====== */
const ES = {
  PENDIENTE: "pendiente",
  EN_REVISION: "en revisión",
  APROBADA: "aprobada",
  RECHAZADA: "rechazada",
  COMPLETADA: "completada",
};
const canon = (s) => (s ?? "").toString().trim().toLowerCase();

const getStatusText = (status) => {
  const s = canon(status);
  const map = {
    [ES.PENDIENTE]: "Pendiente",
    [ES.EN_REVISION]: "En revisión",
    [ES.APROBADA]: "Aprobada",
    [ES.RECHAZADA]: "Rechazada",
    [ES.COMPLETADA]: "Completada",
  };
  return map[s] || "Pendiente";
};
const getStatusBadgeColor = (status) => {
  const s = canon(status);
  if (s === ES.PENDIENTE) return "warning";
  if (s === ES.EN_REVISION) return "info";
  if (s === ES.APROBADA) return "success";
  if (s === ES.RECHAZADA) return "danger";
  if (s === ES.COMPLETADA) return "primary";
  return "secondary";
};
const formatDate = (date) => {
  if (!date) return "No especificada";
  try {
    const jsDate = date?.toDate ? date.toDate() : new Date(date);
    return jsDate.toLocaleString();
  } catch {
    return "Fecha inválida";
  }
};

const RequestDetails = () => {
  const { requestId } = useParams();
  const [request, setRequest] = useState(null);
  const [medication, setMedication] = useState(null);
  const [medLoading, setMedLoading] = useState(false);

  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", variant: "success" });

  const [user, userLoading] = useAuthState(auth);
  const navigate = useNavigate();

  /* ====== Cargar solicitud ====== */
  useEffect(() => {
    const fetchRequest = async () => {
      try {
        setLoading(true);
        const requestRef = doc(db, "requests", requestId);
        const requestDoc = await getDoc(requestRef);
        if (!requestDoc.exists()) {
          setAlert({ show: true, message: "Solicitud no encontrada", variant: "danger" });
          setTimeout(() => navigate("/"), 2000);
          return;
        }

        const data = requestDoc.data();
        setRequest({
          id: requestDoc.id,
          medicationName: data.medicationName || "Suavicort",
          medicationCategory: data.medicationCategory || "No especificada",
          medicationDosage: data.medicationDosage || "No especificada",
          medicationCondition: data.medicationCondition || "No especificada",
          medicationQuantity: data.medicationQuantity || "No especificada",
          medicationExpirationDate: data.medicationExpirationDate || "No especificada",
          medicationDescription: data.medicationDescription || "",
          donorEmail: data.donorEmail || "No especificado",
          donorNotes: data.donorNotes || "",
          userEmail: data.userEmail || "No especificado",
          requesterNotes: data.requesterNotes || "",
          status: canon(data.status) || ES.PENDIENTE, // ← normalizado
          prescriptionUrl: data.prescriptionUrl || null,
          prescriptionName: data.prescriptionName || null,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          userId: data.userId,
          donorId: data.donorId,
          medicationId: data.medicationId || null,
          medicationImageUrl: data.medicationImageUrl || null,
        });
      } catch (e) {
        console.error("Error fetching request:", e);
        setAlert({ show: true, message: "Error al cargar la solicitud", variant: "danger" });
      } finally {
        setLoading(false);
      }
    };

    if (requestId) fetchRequest();
  }, [requestId, navigate]);

  /* ====== Cargar datos del medicamento ====== */
  useEffect(() => {
    const fetchMedication = async () => {
      if (!request?.medicationId) return;
      try {
        setMedLoading(true);
        const medRef = doc(db, "medications", request.medicationId);
        const medDoc = await getDoc(medRef);
        if (medDoc.exists()) setMedication({ id: medDoc.id, ...medDoc.data() });
      } catch (e) {
        console.error("Error fetching medication:", e);
      } finally {
        setMedLoading(false);
      }
    };
    fetchMedication();
  }, [request?.medicationId]);

  /* ====== Upload de receta ====== */
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validTypes = ["application/pdf", "image/jpeg", "image/png"];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(selectedFile.type)) {
      setAlert({ show: true, message: "Solo se permiten archivos PDF, JPEG o PNG", variant: "danger" });
      return;
    }
    if (selectedFile.size > maxSize) {
      setAlert({ show: true, message: "El archivo no debe exceder 5MB", variant: "danger" });
      return;
    }

    setFile(selectedFile);
    setAlert({ show: false, message: "", variant: "success" });

    if (selectedFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => setFilePreview(reader.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setFilePreview(null);
    }
  };

  const handleUploadPrescription = async () => {
    if (!file) {
      setAlert({ show: true, message: "Debes seleccionar un archivo", variant: "danger" });
      return;
    }
    if (!user?.uid) {
      setAlert({ show: true, message: "Usuario no autenticado", variant: "danger" });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const timestamp = Date.now();
      const sanitized = file.name.replace(/\s+/g, "_");
      const fileName = `${timestamp}_${sanitized}`;
      const storagePath = `prescriptions/${user.uid}/${fileName}`;
      const fileRef = ref(storage, storagePath);

      // Metadata para reglas de Storage (contentType)
      const uploadTask = uploadBytesResumable(fileRef, file, {
        contentType: file.type,
        cacheControl: "public,max-age=31536000,immutable",
      });

      uploadTask.on(
        "state_changed",
        (snap) => {
          const pct = (snap.bytesTransferred / snap.totalBytes) * 100;
          setUploadProgress(pct);
        },
        (err) => {
          console.error("Upload error:", err);
          setAlert({ show: true, message: `Error al subir archivo: ${err.message}`, variant: "danger" });
          setUploading(false);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            await updateDoc(doc(db, "requests", requestId), {
              prescriptionUrl: downloadURL,
              prescriptionName: file.name,
              status: ES.EN_REVISION, // guardamos en minúsculas
              updatedAt: serverTimestamp(),
            });

            await sendRequestStatusEmails({
              requestId,
              medicationName: request.medicationName,
              status: ES.EN_REVISION,
              requesterEmail: request.userEmail,
              donorEmail: request.donorEmail,
              actorRole: "solicitante",
            });

            setRequest((prev) => ({
              ...prev,
              prescriptionUrl: downloadURL,
              prescriptionName: file.name,
              status: ES.EN_REVISION,
              updatedAt: new Date(),
            }));

            setAlert({
              show: true,
              message: "Receta médica subida correctamente. La solicitud está en revisión.",
              variant: "success",
            });
          } catch (err) {
            console.error("Error updating document:", err);
            setAlert({ show: true, message: `Error al actualizar la solicitud: ${err.message}`, variant: "danger" });
          } finally {
            resetUploadState();
          }
        }
      );
    } catch (err) {
      console.error("Error starting upload:", err);
      setAlert({ show: true, message: `Error al iniciar la subida: ${err.message}`, variant: "danger" });
      resetUploadState();
    }
  };

  const resetUploadState = () => {
    setFile(null);
    setFilePreview(null);
    setUploading(false);
    setUploadProgress(0);
    const el = document.getElementById("fileInput");
    if (el) el.value = "";
  };

  /* ====== Finalizar solicitud ====== */
  const handleFinalizeRequest = async () => {
    if (!user || user.uid !== request?.userId) {
      setAlert({ show: true, message: "No autorizado", variant: "danger" });
      return;
    }
    setLoading(true);
    try {
      await updateDoc(doc(db, "requests", requestId), {
        status: ES.COMPLETADA,
        updatedAt: serverTimestamp(),
      });

      await sendRequestStatusEmails({
        requestId,
        medicationName: request.medicationName,
        status: ES.COMPLETADA,
        requesterEmail: request.userEmail,
        donorEmail: request.donorEmail,
        actorRole: "solicitante",
      });

      // Registrar en subcolección del donante (si existe esa ruta en reglas)
      if (request.donorId) {
        const colRef = collection(db, "users", request.donorId, "receivedRequests");
        await addDoc(colRef, {
          requestId,
          medicationId: request.medicationId || null,
          medicationName: request.medicationName || null,
          status: ES.COMPLETADA,
          createdAt: serverTimestamp(),
          prescriptionUrl: request.prescriptionUrl || null,
          donorId: request.donorId,
          userId: request.userId,
          userEmail: request.userEmail,
        });
      }

      setAlert({ show: true, message: "Solicitud completada", variant: "success" });
      setTimeout(() => navigate("/my-requests"), 1500); // coincide con tus rutas
    } catch (err) {
      console.error("Error finalizing request:", err);
      setAlert({ show: true, message: "Error al completar la solicitud", variant: "danger" });
    } finally {
      setLoading(false);
    }
  };

  /* ====== Derivados para UI ====== */
  const displayName = request?.medicationName || medication?.name || "Suavicort";
  const displayCategory =
    (request?.medicationCategory && request.medicationCategory !== "No especificada")
      ? request.medicationCategory
      : medication?.category || "No especificada";
  const displayDosage =
    (request?.medicationDosage && request.medicationDosage !== "No especificada")
      ? request.medicationDosage
      : medication?.dosage || "No especificada";
  const displayCondition =
    (request?.medicationCondition && request.medicationCondition !== "No especificada")
      ? request.medicationCondition
      : medication?.condition || "No especificada";
  const displayQuantity =
    (request?.medicationQuantity && request.medicationQuantity !== "No especificada")
      ? request.medicationQuantity
      : (typeof medication?.quantity !== "undefined" ? String(medication.quantity) : "No especificada");
  const displayExpiration =
    (request?.medicationExpirationDate && request.medicationExpirationDate !== "No especificada")
      ? request.medicationExpirationDate
      : medication?.expirationDate || "No especificada";
  const displayDescription = request?.medicationDescription || medication?.description || "";
  const medImageUrl = medication?.imageUrl || request?.medicationImageUrl || null;

  /* ====== Loading states ====== */
  if (userLoading || loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Cargando...</p>
      </Container>
    );
  }
  if (!request) {
    return (
      <Container className="text-center py-5">
        <Alert variant="danger">No se pudo cargar la solicitud</Alert>
      </Container>
    );
  }

  return (
    <Container className="request-details-container py-4">
      <h1 className="text-center mb-4">Detalles de la Solicitud</h1>

      {/* Banner: receta requerida */}
      {canon(request.status) === ES.PENDIENTE && !request.prescriptionUrl && (
        <Alert variant="warning" className="mb-4">
          <strong>Receta requerida.</strong> Para continuar con la solicitud, subí una foto o PDF de la receta médica correspondiente.
        </Alert>
      )}

      {alert.show && (
        <Alert
          variant={alert.variant}
          onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
          dismissible
          className="mb-4"
        >
          {alert.message}
        </Alert>
      )}

      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-light d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Solicitud #{String(requestId).slice(0, 8)}</h5>
          <Badge pill bg={getStatusBadgeColor(request.status)}>
            {getStatusText(request.status)}
          </Badge>
        </Card.Header>

        <Card.Body>
          <ListGroup variant="flush">
            {/* Información del Medicamento */}
            <ListGroup.Item>
              <h5 className="mb-3">Información del Medicamento</h5>
              <Row className="align-items-start">
                <Col md={medImageUrl ? 8 : 12}>
                  <Row>
                    <Col md={6}>
                      <p><strong>Nombre:</strong> {displayName}</p>
                      <p><strong>Categoría:</strong> {displayCategory}</p>
                      <p><strong>Dosis:</strong> {displayDosage}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Condición:</strong> {displayCondition}</p>
                      <p><strong>Cantidad:</strong> {displayQuantity}</p>
                      <p><strong>Vencimiento:</strong> {displayExpiration}</p>
                    </Col>
                  </Row>
                  {displayDescription && (
                    <p className="mt-2"><strong>Descripción:</strong> {displayDescription}</p>
                  )}
                </Col>
                {medImageUrl && (
                  <Col md={4} className="text-center">
                    <div className="med-image-wrapper">
                      <img
                        src={medImageUrl}
                        alt={`Imagen de ${displayName}`}
                        className="img-fluid img-thumbnail"
                        style={{ maxHeight: "220px", objectFit: "contain" }}
                      />
                      {medLoading && (
                        <div className="mt-2">
                          <Spinner animation="border" size="sm" />
                        </div>
                      )}
                    </div>
                  </Col>
                )}
              </Row>
            </ListGroup.Item>

            {/* Información del Donante */}
            <ListGroup.Item>
              <h5 className="mb-3">Información del Donante</h5>
              <p><strong>Email:</strong> {request.donorEmail}</p>
              {request.donorNotes && <p className="mt-2"><strong>Notas:</strong> {request.donorNotes}</p>}
            </ListGroup.Item>

            {/* Información del Solicitante */}
            <ListGroup.Item>
              <h5 className="mb-3">Información del Solicitante</h5>
              <p><strong>Email:</strong> {request.userEmail}</p>
              {request.requesterNotes && <p className="mt-2"><strong>Notas:</strong> {request.requesterNotes}</p>}
            </ListGroup.Item>

            {/* Detalles de la Solicitud */}
            <ListGroup.Item>
              <h5 className="mb-3">Detalles de la Solicitud</h5>
              <Row>
                <Col md={6}>
                  <p><strong>Estado:</strong> {getStatusText(request.status)}</p>
                  <p><strong>Fecha de creación:</strong> {formatDate(request.createdAt)}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Última actualización:</strong> {formatDate(request.updatedAt)}</p>
                  <p>
                    <strong>Receta médica:</strong>{" "}
                    {request.prescriptionUrl ? (
                      <a
                        href={request.prescriptionUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-outline-primary btn-sm ms-1"
                      >
                        {request.prescriptionName || "Ver receta médica"}
                      </a>
                    ) : (
                      <span className="text-muted ms-1">No se ha subido receta médica</span>
                    )}
                  </p>
                </Col>
              </Row>
            </ListGroup.Item>
          </ListGroup>

          {/* Acciones del solicitante */}
          {user?.uid === request.userId && (
            <div className="mt-4">
              {canon(request.status) === ES.PENDIENTE && (
                <div className="border p-3 rounded bg-light">
                  <h5 className="mb-3">Subir Receta Médica</h5>
                  <p className="text-muted">Adjuntá una foto (JPG/PNG) o un PDF de la receta. Máx. 5MB.</p>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Seleccionar archivo</Form.Label>
                    <Form.Control
                      id="fileInput"
                      type="file"
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                      disabled={uploading}
                    />
                    <Form.Text className="text-muted">
                      Formatos aceptados: PDF, JPG, PNG (Máximo 5MB)
                    </Form.Text>
                  </Form.Group>

                  {filePreview && (
                    <div className="mb-3">
                      <p className="fw-bold">Vista previa:</p>
                      <img
                        src={filePreview}
                        alt="Vista previa de receta"
                        className="img-thumbnail"
                        style={{ maxHeight: "200px" }}
                      />
                    </div>
                  )}

                  {file && !filePreview && (
                    <div className="mb-3">
                      <p className="fw-bold">Archivo seleccionado:</p>
                      <p>{file.name}</p>
                    </div>
                  )}

                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="mb-3">
                      <div className="progress">
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{ width: `${uploadProgress}%` }}
                          aria-valuenow={uploadProgress}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        >
                          {Math.round(uploadProgress)}%
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="d-flex gap-2">
                    <Button variant="primary" onClick={handleUploadPrescription} disabled={!file || uploading}>
                      {uploading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                          <span className="ms-2">Subiendo...</span>
                        </>
                      ) : (
                        "Subir Receta"
                      )}
                    </Button>

                    {file && (
                      <Button variant="outline-secondary" onClick={resetUploadState} disabled={uploading}>
                        Cancelar
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {canon(request.status) === ES.EN_REVISION && (
                <div className="border p-3 rounded bg-light">
                  <h5 className="mb-3">Finalizar Solicitud</h5>
                  <p className="mb-3">Tu solicitud está bajo revisión. ¿Has recibido el medicamento?</p>
                  <Button variant="success" onClick={handleFinalizeRequest} disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        <span className="ms-2">Procesando...</span>
                      </>
                    ) : (
                      "Marcar como Completada"
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default RequestDetails;
